import cn from 'classnames';
import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import ReactModal from 'react-modal';

import type { PopupProps } from '@webapp/common/components/popup';
import { Popup } from '@webapp/common/components/popup';
import { useEsc } from '@webapp/common/hooks/use-esc';
import { useLockScroll } from '@webapp/common/hooks/use-lock-scroll';

import css from './modal.css';

const overlayClassName = {
    base: css.modal,
    afterOpen: css.opened,
    beforeClose: css.closing,
};

export type ModalProps = {
    title?: ReactNode;
    visible?: boolean;
    className?: string;
    closeBtn?: boolean;
    onClose?: () => void;
    onCancel?: () => void;
    onAccept?: () => void;
    onRemove?: () => void;
    onPurge?: () => void;
    onAfterClose?: () => void;
    cancelText?: string;
    acceptText?: string;
    style?: CSSProperties;
    portalClassName?: string;
} & Pick<PopupProps, 'titleAlign'>;

export const Modal: FC<ModalProps> = ({
    acceptText,
    cancelText,
    children,
    className,
    closeBtn = true,
    onAccept,
    onAfterClose,
    onCancel,
    onClose,
    onPurge,
    onRemove,
    portalClassName,
    style,
    title,
    visible,
    titleAlign,
}) => {
    const { disable, enable } = useLockScroll();
    const portalRef = useRef<HTMLDivElement>();

    const close = useCallback(() => {
        onAfterClose && onAfterClose();
        enable();
    }, [enable, onAfterClose]);

    useEsc(close);

    useEffect(() => {
        portalRef.current = document.querySelector('#fileManager');
    }, []);

    useLayoutEffect(() => {
        if (visible) {
            disable();
        }
    }, [disable, visible]);

    return (
        <ReactModal
            className={css.container}
            closeTimeoutMS={500}
            isOpen={visible}
            overlayClassName={overlayClassName}
            portalClassName={portalClassName}
            onAfterClose={close}
            onRequestClose={onClose}
            appElement={document.getElementById('app') || document.getElementById('__next')}
        >
            <Popup
                modal
                acceptText={acceptText}
                cancelText={cancelText}
                className={cn(css.popup, className)}
                closeBtn={closeBtn}
                style={style}
                title={title}
                titleAlign={titleAlign}
                onAccept={onAccept}
                onCancel={onCancel}
                onClose={onClose}
                onPurge={onPurge}
                onRemove={onRemove}
            >
                {children}
            </Popup>
        </ReactModal>
    );
};
